import { defineStore } from 'pinia';
import { ref } from 'vue';
import { RouteRecordRaw, useRoute, useRouter } from 'vue-router';

import * as permissionApi from '@/api/permission';
import accountManagementRouter from '@/router/modules/account-management';
import dataRouter from '@/router/modules/data';
import financialManagementRouter from '@/router/modules/financial-management';
import orderManagerRouter from '@/router/modules/order-management';
import systemManagerRouter from '@/router/modules/system-management';
import warrantyRouter from '@/router/modules/warranty';
import type { BasePermission, PermissionListItem, PermsItem } from '@/types/business/permission';

export const usePermissionStore = defineStore('permission', () => {
  const router = useRouter();
  const route = useRoute();
  /**
   * 白名单路由
   */
  const whiteListRouters = ref(['/login']);
  /**
   * 路由列表
   */
  const routers = ref<RouteRecordRaw[]>([]);

  const allPermissionList = ref<PermissionListItem[]>([]);
  const allPermissionListFlat = ref<BasePermission[]>([]);
  const allRoleList = ref<PermsItem[]>([]);
  const currentPermissionList = ref<PermsItem[]>([]);

  // 获取所有权限列表
  const getPermissionList = async (type: 'create' | 'edit' | 'query' = 'query') => {
    // 如果不是管理员且没有权限，直接return
    if (!isAdmin() && !hasPermission('RoleList')) return;

    let res;
    switch (type) {
      case 'create':
        res = await permissionApi.getPermissionListByCreate();
        break;
      case 'edit':
        res = await permissionApi.getPermissionListByEdit();
        break;
      default:
        res = await permissionApi.getPermissionListByQuery();
    }
    try {
      if (res.data.code === 200) {
        allPermissionList.value = res.data.data;
        allPermissionListFlat.value = res.data.data.flatMap((item) => item.permissions);
      } else {
        // 获取权限失败，退回到登录页面
        router.push('/login');
      }
    } catch (error) {
      // 获取权限失败，退回到登录页面
      router.push('/login');
    }
  };

  // 获取当前用户权限
  const getCurrentUserPermissions = async () => {
    const res = await permissionApi.getCurrentUserPermissions();
    try {
      if (res.data.code === 200) {
        currentPermissionList.value = res.data.data.perms;
        allRoleList.value = res.data.data.roles;

        if (isNotActive()) {
          router.push('/reset');
        }

        if (!isNotActive() && (isAdmin() || hasPermission('RoleList'))) {
          await getPermissionList();
        }
      } else {
        if (route.path.includes('reports') || route.path.trim() === '/') {
          return;
        }
        router.push('/login');
      }
    } catch (error) {
      if (route.path.includes('reports') || route.path.trim() === '/') {
        return;
      }
      router.push('/login');
    }
  };

  // 判断是否有权限
  const hasPermission = (permissionCode: string | string[]) => {
    if (Array.isArray(permissionCode)) {
      return currentPermissionList.value.some((p) => permissionCode.includes(p.code));
    }
    return currentPermissionList.value.some((p) => p.code === permissionCode);
  };
  // 判断是否有字段权限
  const hasFieldPermission = (fieldCode: string) => {
    if (isAdmin()) {
      return true;
    }
    const fieldsList = currentPermissionList.value
      .map((item) => {
        return Object.keys(item.fields || {}).reduce(
          (acc, key) => {
            acc[`${item.code}:${key}`] = item.fields[key];
            return acc;
          },
          {} as Record<string, any>,
        );
      })
      .filter(Boolean)
      .flat();

    // let flag = false;
    // for (const item of fieldsList) {
    //   Object.keys(item).forEach(key => {
    //     if (key === fieldCode && item[key] !== 'Hidden') {
    //       flag = true;
    //     }
    //   });
    // }

    const flag = fieldsList.some((item) => {
      return Object.keys(item).some((key) => {
        return key === fieldCode && item[key].type !== 'Hidden';
      });
    });
    return flag;
  };

  // 判断是否具有指定角色
  const hasRole = (roles: string[]) => {
    return allRoleList.value.some((role) => roles.includes(role.code));
  };

  // 判断路由是否有权限访问
  const hasRoutePermission = (meta: any) => {
    // 如果是管理员，可以访问所有路由
    if (isAdmin()) {
      return true;
    }
    // 如果没有配置权限要求，则默认可访问
    if (!meta.role && !meta.permission && !meta.role?.length && !meta.permission?.length) {
      return true;
    }

    const flag =
      (meta.role && hasRole(meta.role as string[])) || (meta.permission && hasPermission(meta.permission as string[]));

    // 如果配置了角色或权限，满足其中一个即可
    return flag;
  };

  // 过滤路由
  const filterRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    return routes.filter((rt) => {
      if (rt.children) {
        rt.children = filterRoutes(rt.children);
      }
      return hasRoutePermission(rt.meta);
    });
  };

  // 判断是否是管理员
  const isAdmin = () => {
    const hasAdmin = allRoleList.value.some((r) => r.code === 'ADMIN');
    return hasAdmin;
  };

  // 判断是否未激活
  const isNotActive = () => {
    const hasNotActive = allRoleList.value.some((r) => r.code === 'UNACTIVATED');
    return hasNotActive;
  };

  const resetPermission = () => {
    currentPermissionList.value = [];
    allRoleList.value = [];
  };

  // 初始化基础路由
  async function initBaseRoutes() {
    const allRoutes = [
      // ...activeCode,
      ...orderManagerRouter,
      ...systemManagerRouter,
      ...accountManagementRouter,
      ...financialManagementRouter,
      ...warrantyRouter,
      ...dataRouter,
    ];
    if (route.path === '/login' || route.path.includes('reports')) return;

    // 获取权限和角色信息
    if (currentPermissionList.value.length === 0) {
      await getCurrentUserPermissions();
    }

    // 过滤路由
    routers.value = filterRoutes(allRoutes);
  }

  return {
    whiteListRouters,
    routers,
    initBaseRoutes,
    getPermissionList,
    allPermissionList,
    allPermissionListFlat,
    allRoleList,
    currentPermissionList,
    getCurrentUserPermissions,
    hasPermission,
    hasRoutePermission,
    hasFieldPermission,
    isAdmin,
    resetPermission,
  };
});
